import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, RouterProvider, createBrowserRouter, useRouteError } from "react-router";
import { signInChanged, whenSignedOut } from "@/api/client";
import { AppShell } from "@/components/app-shell";
import { Loading } from "@/components/page";
import { RequireSession } from "@/components/session";
import { signInHref } from "@/lib/session";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeContext, useTheme } from "@/hooks/use-theme";
import { setRouteTitle } from "@/lib/title";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: true },
  },
});

const TITLES: Record<string, string> = {
  today: "Today",
  leads: "Leads",
  lead: "Lead",
  records: "Records and evidence",
  "mail-run": "Mail run",
  calls: "Call session",
  settings: "Data and settings",
  counties: "Counties",
  account: "Your account",
  accounts: "Accounts",
  "sign-in": "Sign in",
  "set-password": "Choose your password",
  estates: "Estates",
  "sign-up": "Create your account",
  "forgot-password": "Reset your password",
};

function RouteLoading() {
  return (
    <div className="mx-auto w-full max-w-[80rem] px-12 pt-8 max-lg:px-6 max-sm:px-4">
      <Loading />
    </div>
  );
}

/** A plain screen when a page throws, instead of the router's developer message. */
function RouteError() {
  const error = useRouteError() as { message?: string } | undefined;
  return (
    <main className="mx-auto max-w-xl p-6" role="alert">
      <h1 className="font-display text-2xl text-heading">Something went wrong on this page</h1>
      <p className="mt-2 text-sm text-muted">
        The most common cause is a workspace server older than this app. Restart it from the repo folder with
        <code className="mx-1 font-mono">.venv/bin/deedbeacon workspace</code> and reload.
      </p>
      {error?.message ? <pre className="mt-4 overflow-auto rounded-md bg-surface-soft p-3 font-mono text-xs">{error.message}</pre> : null}
      <a href="/today" className="mt-4 inline-block text-link underline-offset-2 hover:underline">Back to Today</a>
    </main>
  );
}

const router = createBrowserRouter([
  // Signing in stands outside every workspace: nothing of one shows before a session.
  {
    path: "sign-in",
    errorElement: <RouteError />,
    HydrateFallback: RouteLoading,
    lazy: () => import("@/pages/sign-in").then((m) => ({ Component: m.SignInPage })),
    handle: { title: TITLES["sign-in"] },
  },
  {
    path: "set-password",
    errorElement: <RouteError />,
    HydrateFallback: RouteLoading,
    lazy: () => import("@/pages/sign-in").then((m) => ({ Component: m.SetPasswordPage })),
    handle: { title: TITLES["set-password"] },
  },
  {
    path: "sign-up",
    errorElement: <RouteError />,
    HydrateFallback: RouteLoading,
    lazy: () => import("@/pages/sign-in").then((m) => ({ Component: m.SignUpPage })),
    handle: { title: TITLES["sign-up"] },
  },
  {
    path: "forgot-password",
    errorElement: <RouteError />,
    HydrateFallback: RouteLoading,
    lazy: () => import("@/pages/sign-in").then((m) => ({ Component: m.ForgotPasswordPage })),
    handle: { title: TITLES["forgot-password"] },
  },
  {
    element: <RequireSession />,
    errorElement: <RouteError />,
    HydrateFallback: RouteLoading,
    children: [
      {
        // The print page stands alone: no app chrome may reach the paper.
        path: "letters/print",
        lazy: () => import("@/pages/print-letters").then((m) => ({ Component: m.PrintLettersPage })),
        handle: { title: "Print letters" },
      },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/today" replace /> },
          // Each screen is its own chunk, loaded on first visit.
          { path: "today", lazy: () => import("@/pages/today").then((m) => ({ Component: m.TodayPage })), handle: { title: TITLES.today } },
          { path: "estates", lazy: () => import("@/pages/estates").then((m) => ({ Component: m.EstatesPage })), handle: { title: TITLES.estates } },
          { path: "leads", lazy: () => import("@/pages/leads").then((m) => ({ Component: m.LeadsPage })), handle: { title: TITLES.leads } },
          { path: "leads/:id", lazy: () => import("@/pages/lead").then((m) => ({ Component: m.LeadPage })), handle: { title: TITLES.lead } },
          {
            path: "leads/:id/records",
            lazy: () => import("@/pages/records").then((m) => ({ Component: m.RecordsPage })),
            handle: { title: TITLES.records },
          },
          { path: "mail-run", lazy: () => import("@/pages/mail-run").then((m) => ({ Component: m.MailRunPage })), handle: { title: TITLES["mail-run"] } },
          { path: "calls", lazy: () => import("@/pages/call-session").then((m) => ({ Component: m.CallSessionPage })), handle: { title: TITLES.calls } },
          { path: "counties", lazy: () => import("@/pages/counties").then((m) => ({ Component: m.CountiesPage })), handle: { title: TITLES.counties } },
          { path: "settings", lazy: () => import("@/pages/settings").then((m) => ({ Component: m.SettingsPage })), handle: { title: TITLES.settings } },
          { path: "account", lazy: () => import("@/pages/account").then((m) => ({ Component: m.AccountPage })), handle: { title: TITLES.account } },
          { path: "accounts", lazy: () => import("@/pages/accounts").then((m) => ({ Component: m.AccountsPage })), handle: { title: TITLES.accounts } },
          { path: "*", lazy: () => import("@/pages/not-found").then((m) => ({ Component: m.NotFoundPage })) },
        ],
      },
    ],
  },
]);

// A session that ends while the app is open (signed out elsewhere, expired, the account
// stopped) sends the app to sign in, forgetting every answer it held.
whenSignedOut(() => {
  const { pathname, search } = router.state.location;
  if (["/sign-in", "/set-password", "/sign-up", "/forgot-password"].includes(pathname)) return;
  signInChanged();
  void router.navigate(signInHref({ pathname, search }), { replace: true }).then(() => queryClient.clear());
});

router.subscribe((state) => {
  const handle = state.matches[state.matches.length - 1]?.route.handle as { title?: string } | undefined;
  setRouteTitle(handle?.title);
});

export function App() {
  const theme = useTheme();
  return (
    <ThemeContext.Provider value={theme}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />
          <Toaster theme={theme.resolved} />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeContext.Provider>
  );
}
